package dev.danko.roblox_craft_ai.components

import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toAttrs
import dev.danko.roblox_craft_ai.client.RobloxCraftApiClient
import dev.danko.roblox_craft_ai.pages.generateSessionToken
import dev.danko.roblox_craft_ai.shared.model.SessionSync
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.ModalDialog
import dev.danko.roblox_craft_ai.uikit.components.ModalDialogConfirmActions
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.autoFocus
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import kotlin.coroutines.resume

val SessionTokenInputStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .fillMaxWidth()
            .minHeight(2.25.cssRem)
            .padding(topBottom = 0.4375.cssRem, leftRight = 1.cssRem)
            .backgroundColor(palette.surfaceElevated)
            .color(palette.textPrimary)
            .border(1.px, LineStyle.Solid, palette.border)
            .borderRadius(0.375.cssRem)
            .fontSize(0.875.cssRem)
            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
            .outline { width(0.px) }
            .transition(
                Transition.of("border-color", 150.ms),
                Transition.of("box-shadow", 150.ms)
            )
    }
    cssRule(":focus") {
        val palette = colorMode.toAppPalette()
        Modifier
            .border(2.px, LineStyle.Solid, palette.accent)
            .boxShadow(0.px, 0.px, 0.px, 3.px, palette.accent.toRgb().copy(alpha = 38))
    }
}

private const val SESSION_TOKEN_KEY = "robloxCraft.sessionToken"

fun loadSavedSessionTokenOrNull(): String? {
    return window.asDynamic().localStorage.getItem(SESSION_TOKEN_KEY) as? String
}

fun saveSessionToken(token: String) {
    window.asDynamic().localStorage.setItem(SESSION_TOKEN_KEY, token)
}

fun clearSessionToken() {
    window.asDynamic().localStorage.removeItem(SESSION_TOKEN_KEY)
}

private suspend fun readClipboardTextOrNull(): String? = suspendCancellableCoroutine { cont ->
    try {
        val clipboard = window.navigator.asDynamic().clipboard
        if (clipboard != null && clipboard != undefined) {
            val promise = clipboard.readText()
            promise.then(
                { text: dynamic -> cont.resume(text as? String) },
                { _: dynamic -> cont.resume(null) }
            )
        } else {
            cont.resume(null)
        }
    } catch (_: Throwable) {
        cont.resume(null)
    }
}

@Composable
fun SessionTokenModal(
    visible: Boolean,
    onCloseRequest: () -> Unit,
    initialToken: String = "",
    onTokenSaved: (newToken: String, connectedSuccess: Boolean, sessionSync: SessionSync?) -> Unit
) {
    if (!visible) return

    var value by remember(visible, initialToken) { mutableStateOf(initialToken) }
    var isLinking by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val palette = com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toAppPalette()

    fun handleSaveAndLink() {
        val trimmed = value.trim()
        if (trimmed.isBlank() || isLinking) return
        isLinking = true
        errorMessage = null
        coroutineScope.launch {
            try {
                val sessionSync = RobloxCraftApiClient.syncRoblox(trimmed)
                isLinking = false
                onTokenSaved(trimmed, true, sessionSync)
            } catch (_: Throwable) {
                isLinking = false
                errorMessage = Txt.sessionToken.validationFailed
            }
        }
    }

    ModalDialog(
        visible = visible,
        onCloseRequest = onCloseRequest,
        title = Txt.sessionToken.title,
        dismissOnBackdropClick = false,
        dismissOnEsc = false,
        footer = ModalDialogConfirmActions(
            confirmText = Txt.sessionToken.saveAndLink,
            cancelText = Txt.common.cancel,
            onConfirm = { handleSaveAndLink() },
            onCancel = onCloseRequest,
            confirmVariant = AppButtonVariant.PRIMARY,
            cancelVariant = AppButtonVariant.OUTLINE,
            confirmLoading = isLinking,
            confirmEnabled = value.isNotBlank()
        )
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(0.75.cssRem)) {
            SpanText(
                Txt.sessionToken.instructions,
                Modifier
                    .fontSize(0.875.cssRem)
                    .color(palette.textSecondary)
                    .lineHeight(1.4)
                    .margin(bottom = 0.5.cssRem)
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                SpanText(
                    Txt.sessionToken.tokenLabel,
                    Modifier
                        .fontSize(0.8.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .color(palette.textSecondary)
                )
                AppButton(
                    text = Txt.common.help,
                    iconLeft = "🔗",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = {
                        window.open("https://create.roblox.com/docs", "_blank")
                    }
                )
            }

            Input(
                type = InputType.Text,
                attrs = SessionTokenInputStyle.toAttrs {
                    placeholder(Txt.sessionToken.tokenPlaceholder)
                    value(value)
                    autoFocus()
                    onInput { evt ->
                        value = evt.value
                        errorMessage = null
                    }
                    onKeyDown { evt ->
                        if (evt.key == "Enter") {
                            handleSaveAndLink()
                        }
                    }
                }
            )

            Row(
                modifier = Modifier.fillMaxWidth().gap(0.5.cssRem).margin(top = 0.25.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                AppButton(
                    text = Txt.sessionToken.pasteFromClipboard,
                    iconLeft = "📋",
                    variant = AppButtonVariant.SECONDARY,
                    size = AppButtonSize.SM,
                    onClick = {
                        coroutineScope.launch {
                            val pasted = readClipboardTextOrNull()
                            if (!pasted.isNullOrBlank()) {
                                value = pasted.trim()
                                errorMessage = null
                            }
                        }
                    }
                )
                AppButton(
                    text = Txt.sessionToken.generateNew,
                    iconLeft = "🔄",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = {
                        value = generateSessionToken()
                        errorMessage = null
                    }
                )
            }

            if (errorMessage != null) {
                SpanText(
                    "❌ $errorMessage",
                    Modifier
                        .fontSize(0.8.cssRem)
                        .color(palette.statusFailed)
                        .padding(topBottom = 0.25.cssRem)
                )
            }
        }
    }
}
